import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { useConfig } from 'Context/ConfigContext';
import { CourseCard, BADGE_VARIANTS } from 'Components/CourseCard';
import CourseFilter from 'Components/CourseFilter';
import { List, Text, Box, Flex } from 'Components/Base';
import { getAppUIVariantConfig } from 'Util/uiVariantSet';

import { COURSE_TYPES } from '../../../../Util/constants';
import EmptySearchResult from '../EmptySearchResult';

const {
  CourseListCard,
  CourseListLayout,
  CourseFilter: CourseFilterVaraint,
} = getAppUIVariantConfig();

const CourseListTitle = ({
  t,
  title,
  subtitle,
  sortLabel,
  onFilterChange,
  filterOptions,
  selectedFilters,
  expiredCoursesCount,
  ...restProps
}) => {
  const { myCourses } = useConfig();
  const handleFilterChange = (value, index) => {
    const filter = [...selectedFilters];
    filter[index] = value;
    onFilterChange(filter);
  };

  return (
    <Flex justifyContent={'space-between'} alignItems={'center'} {...restProps}>
      <Flex alignItems={'baseline'}>
        <Text textStyle={'subtitle1'} mr={2}>
          {title}
        </Text>
      </Flex>
      {myCourses.enableCourseFilterAndSort && (
        <Fragment>
          <CourseFilter
            courseFilterVariant={CourseFilterVaraint}
            expiredCoursesCount={expiredCoursesCount}
            selectedFilters={selectedFilters}
            onFilterChange={CourseFilterVaraint === 'SlideUp' ? handleFilterChange : onFilterChange}
            dialogMessage={`${t('FILTER.EXPIRED_COURSES_COUNT', { expiredCoursesCount })}`}
            filterOptions={filterOptions}
          />
        </Fragment>
      )}
    </Flex>
  );
};

const renderCourseItem = (course, onCourseCardClick, t, variant, eBookCourseRegex) => {
  const { courseInfo, classroomTime } = course;
  const {
    remainingTime,
    classroomTimeInfo,
    type: timeType,
    isValidRemainingTime,
  } = classroomTime || {};
  const { courseType, courseCode } = classroomTimeInfo || {};
  const {
    classroomMode,
    coverImageUrl,
    id,
    title,
    courseCode: courseCodeInfo,
    classroomProgress: { progressPercent, expiryDate, lastEnterAt },
    totalActiveLearningModes,
    mode,
  } = courseInfo || {};
  const defaultImageUrl =
    variant === 'ANY'
      ? '/assets/images/defaultCourseThumbnail/empty-thumbnail-ANY.png'
      : '/assets/images/defaultCourseThumbnail/empty-thumbnail.png';
  const name = courseCodeInfo
    ? `course-card-list-course-code-${courseCodeInfo}`
    : `course-card-list`;
  const badgeVariant = eBookCourseRegex?.test(courseCodeInfo)
    ? BADGE_VARIANTS.EBOOK
    : totalActiveLearningModes > 1
    ? BADGE_VARIANTS.LEARNING_MODE
    : BADGE_VARIANTS.NONE;

  return (
    <CourseCard
      CourseCardVariant={CourseListCard}
      key={course.id}
      badgeVariant={badgeVariant}
      imageUrl={coverImageUrl}
      defaultImageUrl={defaultImageUrl}
      title={title}
      time={remainingTime}
      timeType={timeType}
      isValidRemainingTime={isValidRemainingTime}
      lastEnter={lastEnterAt}
      expDate={expiryDate}
      onClick={() => onCourseCardClick(id, mode)}
      progress={Math.floor(progressPercent ?? 0)}
      footer={
        courseType === COURSE_TYPES.PACK && t('COURSE_CARD.FOOTER.SMALL', { packName: courseCode })
      }
      trialMode={classroomMode === 'TRIAL'}
      name={name}
      totalActiveLearningModes={totalActiveLearningModes}
    />
  );
};

const CourseList = ({
  courses,
  onCourseCardClick,
  title,
  sortLabel,
  filterOptions,
  onFilterChange,
  selectedFilters,
  expiredCoursesCount,
  searchResults,
  ...restProps
}) => {
  const { t } = useTranslation('myCourses');
  const { uiVariant, eBookCourseRegex = /^.*E$/ } = useConfig();

  const courseFilter = selectedFilters[0];
  const isExmptySearchResult = searchResults.length === 0;

  return (
    <Box id="course-list" mt={4} px={4} {...restProps}>
      <CourseListTitle
        t={t}
        title={title}
        sortLabel={sortLabel}
        filterOptions={filterOptions}
        onFilterChange={onFilterChange}
        selectedFilters={selectedFilters}
        expiredCoursesCount={expiredCoursesCount}
        mb={4}
      />
      {courses && courses.length > 0 ? (
        <List
          listLayout={CourseListLayout}
          items={courses}
          renderItem={(course) =>
            renderCourseItem(course, onCourseCardClick, t, uiVariant, eBookCourseRegex)
          }
          minMaxWidth={'164px'}
          columns={[1, 2]}
        />
      ) : (
        <>
          {isExmptySearchResult ? (
            <EmptySearchResult mt={8} />
          ) : (
            <Text mt={8} textAlign={'center'}>
              {t(`COURSE_LIST.EMPTY.${courseFilter}`)}
            </Text>
          )}
        </>
      )}
    </Box>
  );
};

CourseListTitle.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  sortLabel: PropTypes.string,
  onFilterChange: PropTypes.func,
  filterOptions: PropTypes.object,
  selectedFilters: PropTypes.array,
  expiredCoursesCount: PropTypes.number,
};

CourseList.propTypes = {
  courses: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      courseInfo: PropTypes.shape({
        coverImageUrl: PropTypes.string,
        title: PropTypes.string.isRequired,
        time: PropTypes.string,
        expDate: PropTypes.string,
        classroomProgress: PropTypes.shape({
          progressPercent: PropTypes.number,
          expiryDate: PropTypes.string,
        }),
      }),
      classroomTime: PropTypes.shape({
        remainingTime: PropTypes.number,
      }),
    }).isRequired
  ),
  title: PropTypes.string,
  sortLabel: PropTypes.string,
  filterOptions: PropTypes.object,
  onCourseCardClick: PropTypes.func,
  onFilterChange: PropTypes.func,
  expiredCoursesCount: PropTypes.number,
  selectedFilters: PropTypes.array,
  searchResults: PropTypes.array,
};

CourseList.defaultProps = {
  courses: [],
  onCourseCardClick: () => {},
  onFilterChange: () => {},
};

export default CourseList;
